
import { findKey } from '../EkonomiskAnalysQueryUtils';
import query from './EkonomiskAnalysRadBaseradQuery.gql';
import {
  AnalysRadBaseradQueryVariables as IVariables,
  AnalysRadBaseradQuery as IData,
  AnalysRadBaseradQuery_ekanalysresultatperid_columns as IRadBaseradColumnResult,
  AnalysRadBaseradQuery_ekanalysresultatperid_columns_fieldinfo,
} from './_QueryTypes/AnalysRadBaseradQuery';
import { getFieldInfoValue } from '../../Utils/fieldInfoUtils';
import Logger from '../../../../../../../Utils/Logger';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import { datesToTimeInterval } from '../../Utils/convertUtils';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import { IEkonomiskAnalysFilledDates } from '../../Components/AnalysSettingsHandler/AnalysSettingsHandler';
import { IColumn } from '../../Components/EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import useHandledQuery from '../../../../../../../Components/QueryComponents/HandledQuery/HandledQueryHook';
import { NEW_ANALYS_RAPPORT_ID } from '../../Components/SideMenu/Components/CreateNewAnalys/CreateNewAnalysUtils';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { useMemo, type JSX } from 'react';

interface IProps {
  children: (data: IRadBaseradAnalys, hideTable?: boolean) => JSX.Element;
  id: string;
  codePrivilegesId: string;
  dates: IEkonomiskAnalysFilledDates;
  endastBokfort?: boolean;
  forceHelarsjamforelse: boolean | null;
  userUrval: IUIUrval | null;
  settingsChanged: boolean;
  setRefetchAnalys: (resetAnalys: () => void) => void;
  grundurvalId: string | undefined;
  skip?: boolean;
}

export interface IRow {
  level: number;
  filtered: boolean;
  isZeroRow: boolean;
  hasData: boolean; // Definierat som att det finns en benämning.. används för styling
  values: string[];
  rowType: string;
  urvalId: string | null;
  bold: boolean;
  dimRowID: number;
  rowDef: string | null;
}

export interface IRadBaseradAnalys {
  isEmpty: boolean;
  rows: IRow[];
  columns: IColumn[];
}

const isPercentColumn = (rawColumn: IRadBaseradColumnResult): boolean => {
  const { fieldinfo } = rawColumn;

  if (fieldinfo) {
    const fieldOpValue = getFieldInfoValue('FieldOperator', fieldinfo);
    if (fieldOpValue === '%') {
      return true;
    }
  }

  return false;
};

const getInverted = (
  fieldInfo:
    | AnalysRadBaseradQuery_ekanalysresultatperid_columns_fieldinfo[]
    | null
): boolean => {
  const vandTackenValue = fieldInfo?.find(
    info => info.name === 'VAND_TECKEN'
  )?.value;

  return vandTackenValue && vandTackenValue.__typename === 'TEDMixedBoolean'
    ? vandTackenValue.asBoolean || false
    : false;
};

const extractColumnData = (
  rawColumns: IRadBaseradColumnResult[]
): IColumn[] => {
  const columns: IColumn[] = [];

  for (let i = 0; i < rawColumns.length; i++) {
    if (rawColumns != null) {
      const { visible, rubrik1, rubrik2, rubrik3, fieldinfo, fieldtype } =
        rawColumns[i];

      columns.push({
        visible: visible != null ? visible : false,
        title1: rubrik1 || '',
        title2: rubrik2 || '',
        title3: rubrik3 || '',
        fieldType: fieldtype || '',
        fieldInfo: fieldinfo || [],
        inverted: getInverted(fieldinfo),
        isPercent: isPercentColumn(rawColumns[i]),
      });
    }
  }

  return columns;
};

const extractRowData = (
  rawColumns: IRadBaseradColumnResult[],
  benamningIndex: number
): IRow[] => {
  const rows: IRow[] = [];
  for (let col = 0; col < rawColumns.length; col++) {
    const column = rawColumns[col].data;
    if (!column) {
      Logger.error('No data in column');
    } else {
      const dataKey = findKey(column) || '';
      const columnData = (column as unknown as Record<string, unknown[]>)[dataKey];

      for (let row = 0; row < columnData.length; row++) {
        if (!rows[row]) {
          rows[row] = {
            dimRowID: 0,
            level: 0,
            filtered: false,
            values: [],
            hasData: true,
            rowType: '',
            urvalId: null,
            isZeroRow: true,
            bold: false,
            rowDef: null,
          };
        }

        const val = columnData[row] as string;
        if (col > benamningIndex && parseFloat(val) !== 0) {
          rows[row].isZeroRow = false;
        }

        if (rawColumns[col].fieldtype === 'ekreportradlevel') {
          rows[row].level = columnData[row] as number;
        } else if (rawColumns[col].fieldtype === 'ekreportfiltered') {
          rows[row].filtered = columnData[row] as boolean;
        } else if (rawColumns[col].fieldtype === 'ekreportradbenamning') {
          rows[row].hasData = columnData[row] != null;
        } else if (rawColumns[col].fieldtype === 'ekreportrowtype') {
          rows[row].rowType = columnData[row] as string;
          // Rows that are lines (thin or thick) should not be shown
          if (rows[row].rowType === 'L' || rows[row].rowType === 'F') {
            rows[row].filtered = true;
          }
        } else if (rawColumns[col].fieldtype === 'ekreportrowid') {
          rows[row].urvalId = columnData[row] as string | null;
        } else if (rawColumns[col].fieldtype === 'ekreportfetstil') {
          rows[row].bold = val as unknown as boolean;
        } else if (rawColumns[col].fieldtype === 'ekreportdimrowid') {
          rows[row].dimRowID = val as unknown as number;
        } else if (rawColumns[col].fieldtype === 'ekreportrowdefinition') {
          rows[row].rowDef = val as string | null;
        }

        rows[row].values[col] = val;
      }
    }
  }

  return rows;
};

const EkonomiskAnalysRadBaseradQuery = (props: IProps) => {
  const variables = useMemo(
    () => ({
      id: props.id,
      codePrivilegesId: props.codePrivilegesId,
      tidsintervall: datesToTimeInterval(props.dates),
      endastBokfort: props.endastBokfort,
      force_helarsjamforelse: props.forceHelarsjamforelse,
      userUrval: tryConvertUIUrvalToTEDUrval(props.userUrval),
      grundurval_id: props.grundurvalId,
    }),
    [
      props.id,
      props.codePrivilegesId,
      props.endastBokfort,
      props.dates,
      props.userUrval,
      props.grundurvalId,
      props.forceHelarsjamforelse,
    ]
  );

  const queryResult = useHandledQuery<IData, IVariables>(
    query,
    {
      variables: variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip:
        props.skip ||
        props.settingsChanged ||
        props.id === NEW_ANALYS_RAPPORT_ID,
    },
    { loadingComponent: <EDAvenyProgress /> },
    { setRefetch: props.setRefetchAnalys }
  );

  if (props.settingsChanged || props.id === NEW_ANALYS_RAPPORT_ID) {
    const analys: IRadBaseradAnalys = {
      isEmpty: true,
      columns: [],
      rows: [],
    };
    return <>{props.children(analys, true)}</>;
  }

  if (!queryResult.data) {
    return queryResult.renderComponent;
  }

  const data = queryResult.data;

  if (!data.ekanalysresultatperid || !data.ekanalysresultatperid.columns) {
    return <div>Ingen analys hittades</div>;
  }

  const benamningIndex = data.ekanalysresultatperid.columns.findIndex(
    col => col.fieldtype === 'ekreportradbenamning'
  );

  const columns = extractColumnData(data.ekanalysresultatperid.columns);
  const rows = extractRowData(
    data.ekanalysresultatperid.columns,
    benamningIndex
  );

  const analys: IRadBaseradAnalys = {
    isEmpty: rows.every(row => row.isZeroRow),
    columns,
    rows,
  };

  return props.children(analys);
};

export default EkonomiskAnalysRadBaseradQuery;
